import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, Slider, TextField } from '@material-ui/core';
import Lock from '@material-ui/icons/Lock';
import { updateEditorItem } from '../../Reducers/Configurator/Actions';
import { getDefaultCanvasSize, getSelectedEditorItem } from '../../Reducers/Configurator/Selectors';
import { MIN_ITEM_WIDTH_CM } from '../../constants/editorDefaults';
import { cmToMm, mmToCm, DEFAULT_CANVAS_HEIGHT_MM, DEFAULT_CANVAS_WIDTH_MM } from '../../utils/editorUnits';
import { computeSizeFromLockedWidth, computeSizeFromLockedHeight } from '../../utils/itemSizing';
import { getItemNaturalSizeMm } from '../../utils/editorItemLayout';
import type { EditorItem } from '../../Reducers/Configurator/types';
import SectionTitle from './SectionTitle';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(2),
  },
  fields: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sliderRow: {
    marginTop: theme.spacing(1.5),
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(2),
  },
  field: {
    flex: 1,
  },
  lockIcon: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  sliderLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
  },
}));

function getNaturalSize(item: EditorItem) {
  return getItemNaturalSizeMm(item);
}

type DimensionFieldProps = {
  label: string;
  valueCm: number;
  onChange: (cm: number) => void;
  classes: ReturnType<typeof useStyles>;
};

function DimensionField({ label, valueCm, onChange, classes }: DimensionFieldProps) {
  const [draft, setDraft] = React.useState<string | null>(null);
  const editing = draft !== null;

  const commit = () => {
    const raw = draft ?? '';
    setDraft(null);
    const parsed = parseFloat(raw);
    if (Number.isFinite(parsed) && parsed >= MIN_ITEM_WIDTH_CM) {
      onChange(parsed);
    }
  };

  return (
    <TextField
      className={classes.field}
      label={label}
      type="number"
      size="small"
      variant="outlined"
      value={editing ? draft : valueCm}
      onFocus={() => setDraft(String(valueCm))}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          commit();
          (e.target as HTMLInputElement).blur();
        }
      }}
      inputProps={{ min: 0, step: 0.1 }}
    />
  );
}

export default function SizeSection() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const item = useSelector(getSelectedEditorItem);
  const canvasSize = useSelector(getDefaultCanvasSize);

  if (!item || (item.type !== 'text' && item.type !== 'image')) return null;

  const widthCm = mmToCm(item.width);
  const heightCm = mmToCm(item.height);
  const maxWidthCm = Math.max(MIN_ITEM_WIDTH_CM, mmToCm(canvasSize.width || DEFAULT_CANVAS_WIDTH_MM));
  const canvasLimits = {
    maxHeight: canvasSize.height || DEFAULT_CANVAS_HEIGHT_MM,
    maxWidth: canvasSize.width || DEFAULT_CANVAS_WIDTH_MM,
  };
  const sliderWidthCm = Math.min(Math.max(widthCm, MIN_ITEM_WIDTH_CM), maxWidthCm);

  const updateWidth = (cm: number) => {
    const width = cmToMm(cm);
    const natural = getNaturalSize(item);
    const patch = computeSizeFromLockedWidth(natural.width, natural.height, width, canvasLimits.maxHeight);
    dispatch(updateEditorItem(item.id, { ...patch, lockedDimension: 'width' }));
  };

  const updateHeight = (cm: number) => {
    const height = cmToMm(cm);
    const natural = getNaturalSize(item);
    const patch = computeSizeFromLockedHeight(natural.width, natural.height, height, canvasLimits.maxWidth);
    dispatch(updateEditorItem(item.id, { ...patch, lockedDimension: 'height' }));
  };

  return (
    <div className={classes.section}>
      <SectionTitle>{t('sizeSection.title')}</SectionTitle>
      <div className={classes.fields}>
        <DimensionField
          key={`${item.id}-width`}
          label={t('sizeSection.widthLabel')}
          valueCm={widthCm}
          onChange={updateWidth}
          classes={classes}
        />
        <span className={classes.lockIcon} aria-label={t('sizeSection.aspectRatioLocked')}>
          <Lock fontSize="small" />
        </span>
        <DimensionField
          key={`${item.id}-height`}
          label={t('sizeSection.heightLabel')}
          valueCm={heightCm}
          onChange={updateHeight}
          classes={classes}
        />
      </div>
      <div className={classes.sliderRow}>
        <Slider
          value={sliderWidthCm}
          min={MIN_ITEM_WIDTH_CM}
          max={maxWidthCm}
          step={0.1}
          onChange={(_, value) => updateWidth(value as number)}
          aria-label={t('sizeSection.widthSlider')}
        />
        <span className={classes.sliderLabel}>{t('sizeSection.sliderLabel')}</span>
      </div>
    </div>
  );
}
